import * as React from 'react'

import { clsx } from 'clsx'

import {
  Card,
  ChannelIcon,
  Flex,
  NotifyMeIcon,
  NotifyMePublished,
  Text,
} from '@buffer-mono/popcorn'

import { PostMediaAsset } from '~publish/components/PostCard/PostMedia/PostMediaAsset'
import { ApprovalBadge, DraftBadge } from '~publish/components/PostStatusBadge'
import { PostTypeBadge, PostTypeIcon } from '~publish/components/PostType'
import type { CommentsPostCard_PostData } from '~publish/pages/Community/fragments/CommentsPostCard_Post'
import type { PostStatus } from '~publish/pages/Community/graphqlEnums'
import { isChannelIconService } from '~publish/pages/Community/util/isChannelIconService'

import type { StyleWithCssVars } from './types'
import { useFormattedPostTime } from './useFormattedPostTime'
import { usePostMiniatureLayout } from './usePostMiniatureLayout'
import { getPostDisplayText } from './utils'
import sharedBadgeStyles from '../shared/CarouselBadge.module.css'

import styles from './PostMiniature.module.css'

import type { JSX } from 'react'

type StatusBadgeProps = {
  status: PostStatus
}

function StatusBadge({ status }: StatusBadgeProps): JSX.Element | null {
  if (status !== 'draft' && status !== 'needs_approval') return null

  return (
    <div className={styles.statusBadge}>
      {status === 'needs_approval' && <ApprovalBadge size="small" />}
      {status === 'draft' && <DraftBadge size="small" />}
    </div>
  )
}

type MiniatureHeaderProps = {
  showChannel: boolean
  channelService: CommentsPostCard_PostData['channelService']
  channelName?: string | null
  dueAt?: string | null
  formattedTime: string
  metadata: CommentsPostCard_PostData['metadata']
  notifyIcon: React.ReactNode
}

function MiniatureHeader({
  showChannel,
  channelService,
  channelName,
  dueAt,
  formattedTime,
  metadata,
  notifyIcon,
}: MiniatureHeaderProps): JSX.Element {
  return (
    <header className={styles.header}>
      <Flex align="center" gap="xs" className={styles.headerContent}>
        {showChannel && (
          <>
            <span
              className={styles.channelIcon}
              data-channel={channelService}
              aria-hidden
            >
              {isChannelIconService(channelService) && (
                <ChannelIcon type={channelService} size={10} />
              )}
            </span>
            <Text
              size="sm"
              weight="medium"
              className={styles.channelName}
              translate="no"
            >
              {channelName}
            </Text>
          </>
        )}
        {formattedTime && (
          <Text
            as="time"
            dateTime={dueAt ?? undefined}
            size="sm"
            weight="medium"
            className={styles.dueAt}
          >
            {formattedTime}
          </Text>
        )}
        {(metadata?.type === 'reel' || metadata?.type === 'story') && (
          <PostTypeBadge type={metadata.type} size="small" />
        )}
        {notifyIcon}
      </Flex>
      {metadata?.type !== 'reel' && metadata?.type !== 'story' && (
        <PostTypeIcon
          type={metadata?.type}
          size="small"
          className={styles.postType}
        />
      )}
    </header>
  )
}

type PostBodyProps = {
  text: string | null | undefined
  condensed: boolean
  hasTags: boolean
  tagsToRender: CommentsPostCard_PostData['tags']
  tagsRemainder: CommentsPostCard_PostData['tags']
}

function PostBody({
  text,
  condensed,
  hasTags,
  tagsToRender,
  tagsRemainder,
}: PostBodyProps): JSX.Element {
  const hasText = text && text.trim().length > 0

  return (
    <div className={styles.body}>
      {!condensed &&
        (hasText ? (
          <Text className={styles.textContent} translate="no">
            {text}
          </Text>
        ) : (
          <Text className={styles.textContent} color="subtle">
            <em>This post has no caption</em>
          </Text>
        ))}
      {hasTags && (
        <Flex gap="2xs" align="end" className={styles.tags}>
          {tagsToRender.map((tag) => {
            const tagStyle: StyleWithCssVars = {
              '--tag-color': tag.color,
            }
            return <div key={tag.id} className={styles.tag} style={tagStyle} />
          })}
          {tagsRemainder.length > 0 && (
            <span className={styles.extraTagsText}>
              +{tagsRemainder.length}
            </span>
          )}
        </Flex>
      )}
    </div>
  )
}

type PostAssetsProps = {
  assets: CommentsPostCard_PostData['assets']
  hasMultipleAssets: boolean
  hasOnlyMediaContent: boolean
  condensed: boolean
}

function PostAssets({
  assets,
  hasMultipleAssets,
  hasOnlyMediaContent,
  condensed,
}: PostAssetsProps): JSX.Element {
  return (
    <div className={styles.assetsContainer}>
      <PostMediaAsset
        asset={assets[0]}
        className={clsx(
          styles.assets,
          hasOnlyMediaContent && !condensed && styles.assetsLarge,
        )}
      />
      {hasMultipleAssets && (
        <span
          className={clsx(
            sharedBadgeStyles.carouselBadge,
            hasOnlyMediaContent && !condensed
              ? sharedBadgeStyles.large
              : sharedBadgeStyles.medium,
          )}
        >
          +{assets.length - 1}
        </span>
      )}
    </div>
  )
}

interface PostMiniatureProps {
  condensed?: boolean
  post: CommentsPostCard_PostData
  className?: string
  /** When false (singular channel view), channel icon and name are hidden */
  showChannel?: boolean
}

export const PostMiniature: React.FC<PostMiniatureProps> = ({
  condensed,
  post,
  className,
  showChannel = true,
}) => {
  const { assets, channelService, channel, dueAt, metadata } = post

  const displayText = getPostDisplayText(post)

  const formattedTime = useFormattedPostTime(dueAt)
  const {
    hasMultipleAssets,
    hasOnlyMediaContent,
    hasTags,
    renderAssets,
    renderBody,
    tagsToRender,
    tagsRemainder,
    cardStyle,
  } = usePostMiniatureLayout(post, !!condensed)

  const { schedulingType, status, notificationStatus } = post
  const notifyIcon = React.useMemo(() => {
    if (schedulingType === 'notification') {
      if (status === 'sent' && notificationStatus === 'markedAsPublished') {
        return <NotifyMePublished size="xsmall" className={styles.notifyIcon} />
      }
      return <NotifyMeIcon size="xsmall" className={styles.notifyIcon} />
    }
    return null
  }, [schedulingType, status, notificationStatus])

  return (
    <Card asChild padding="none">
      <article
        className={clsx(styles.card, className)}
        aria-label={`Post for ${
          channel?.displayName ?? channel?.name ?? 'unknown channel'
        }`}
        data-condensed={condensed}
        data-status={post.status}
        data-media-only={hasOnlyMediaContent && !condensed}
        style={cardStyle}
      >
        <StatusBadge status={post.status} />
        <MiniatureHeader
          showChannel={showChannel}
          channelService={channelService}
          channelName={channel?.displayName ?? channel?.name}
          dueAt={dueAt}
          formattedTime={formattedTime}
          metadata={metadata}
          notifyIcon={notifyIcon}
        />
        {renderBody && (
          <PostBody
            text={displayText}
            condensed={!!condensed}
            hasTags={hasTags}
            tagsToRender={tagsToRender}
            tagsRemainder={tagsRemainder}
          />
        )}
        {renderAssets && (
          <PostAssets
            assets={assets}
            hasMultipleAssets={hasMultipleAssets}
            hasOnlyMediaContent={hasOnlyMediaContent}
            condensed={!!condensed}
          />
        )}
      </article>
    </Card>
  )
}
